if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // ADA #57: Listen to thumbnail strip scroll to update visibility as user scrolls
        const slider = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (slider) {
          slider.addEventListener('scroll', debounce(this.setSlideVisibility.bind(this), 100));
        }

        // ADA #57: Set initial slide visibility on page load
        window.addEventListener('load', () => {
          setTimeout(() => {
            this.setSlideVisibility();
          }, 300);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // ADA #57: Update slide visibility for screen readers
        this.setSlideVisibility();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);

        // ADA #57: Update slide visibility after switching media (delay allows scroll to settle)
        setTimeout(() => {
          this.setSlideVisibility();
        }, 150);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      // ADA #57: Hide non-visible slides from screen readers and keyboard navigation
      setSlideVisibility() {
        // --- Main gallery slides: only the active slide is visible ---
        if (this.elements.viewer) {
          this.elements.viewer.querySelectorAll('[id^="Slide-"]').forEach((slide) => {
            const isVisible = slide.classList.contains('is-active');
            slide.setAttribute('aria-hidden', String(!isVisible));
            slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
              el.tabIndex = isVisible ? 0 : -1;
            });
          });
        }

        // --- Thumbnail strip: use rect comparison to determine what's in the scroll viewport ---
        if (!this.elements.thumbnails) return;

        const slider = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (!slider) return;

        const sliderRect = slider.getBoundingClientRect();

        this.elements.thumbnails.querySelectorAll('[id^="Slide-Thumbnails-"]').forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();

          // Visible if substantially within the slider container's horizontal bounds.
          // Both rects are in the same coordinate space so page scroll is irrelevant.
          const isVisible =
            slideRect.right > sliderRect.left + 1 &&
            slideRect.left < sliderRect.right - 1;

          const button = slide.querySelector('button');
          slide.setAttribute('aria-hidden', String(!isVisible));
          if (button) button.tabIndex = isVisible ? 0 : -1;
        });
      }
    }
  );
}